import { useMemo } from 'react';

import { useMatchMedia } from '../../hooks';

import { Link, Svg } from '../../snippets';

export function HeroContent({
  aboveTheFold,
  isActiveSlide,
  isFirstSlide,
  slide,
  fullSizeContent,
}) {
  const isMobileViewport = useMatchMedia('(max-width: 1023px)');

  const { button, content, text } = slide;
  const {
    colorDesktop,
    colorMobile,
    heading,
    hideHeadingDesktop,
    hideHeadingMobile,
    subheading,
    superheading,
    html,
    svg,
  } = { ...text };
  const {
    alignmentMobile,
    alignmentDesktop,
    darkOverlay,
    maxWidthMobile,
    maxWidthDesktop,
    positionMobile,
    positionDesktop,
  } = {
    ...content,
  };
  const fullSizeContentPaddingClass = fullSizeContent ? '' : 'p-4 md:p-8 xl:p-12';
  const fullSizeContentClass = fullSizeContent ? 'w-full h-full' : '';
  const alignmentClasses = `${alignmentMobile} ${alignmentDesktop}`;
  const positionClasses = `${positionMobile} ${positionDesktop}`;
  const maxWidthContentClasses = `${maxWidthMobile} ${maxWidthDesktop}`;
  const darkOverlayClass = darkOverlay ? 'bg-[rgba(0,0,0,0.2)]' : '';
  const hiddenHeadingMobileClass =
    'max-md:absolute max-md:z-[-1] max-md:opacity-0 max-md:pointer-events-none';
  const hiddenHeadingDesktopClass =
    'md:absolute md:z-[-1] md:opacity-0 md:pointer-events-none';
  const hiddenHeadingClasses = `${
    hideHeadingMobile ? hiddenHeadingMobileClass : ''
  } ${hideHeadingDesktop ? hiddenHeadingDesktopClass : ''} ${alignmentClasses}`;
  const hiddenButtonClasses = `${
    button?.hideButtonsMobile ? 'max-md:hidden' : ''
  } ${button?.hideButtonsDesktop ? 'md:hidden' : ''}`;
  const textColorClasses = `${colorMobile} ${colorDesktop}`;
  const svgWidth = isMobileViewport ? svg?.svgMobileWidth : svg?.svgDesktopWidth;
  const svgHeight = isMobileViewport ? svg?.svgMobileHeight : svg?.svgDesktopHeight;

  const headingWithBreaks = useMemo(() => {
    const splitHeading = heading?.split('\n');
    if (splitHeading?.length === 1) return heading;
    return splitHeading?.reduce((acc, line, index, arr) => {
      acc.push(<span key={index}>{line}</span>);
      if (index < arr.length - 1) acc.push(<br key={`br${index}`} />);
      return acc;
    }, []);
  }, [heading]);
 
  return (
    <div
      className={`pointer-events-none absolute inset-0 flex h-full w-full ${fullSizeContentPaddingClass} ${positionClasses} ${darkOverlayClass}`}
    >
      <div
        className={`pointer-events-auto relative z-[2] flex flex-col ${fullSizeContentClass} first:${alignmentClasses} ${maxWidthContentClasses} ${textColorClasses}`}
      >
        <div className={`${hiddenHeadingClasses} ${fullSizeContentClass}`}>
          {html && (
            <div
              className={`mx-auto flex flex-col [&>:first-child]:mt-0 [&>:last-child]:mb-0 [&_a]:underline [&_h1]:mb-6 [&_h2]:mt-8 [&_h2]:mb-5 [&_h3]:mb-4 [&_h3]:mt-6 [&_h4]:mb-4 [&_h4]:mt-4 [&_h5]:mb-4 [&_h5]:mt-2 [&_h6]:mb-4 [&_li>p]:mb-0 [&_li]:mb-2 [&_ol>li]:list-decimal [&_ol]:mb-4 [&_ol]:pl-8 [&_p]:mb-4 [&_ul>li]:list-disc [&_ul]:mb-4 [&_ul]:pl-8 ${fullSizeContentClass}`}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: html }}
            />
          )}

          {svg?.svgName && (
            <Svg
              src={`/svgs/noprecache/content/${svg?.svgName}.svg#${svg?.svgName}`}
              alt={`${svg?.svgAlt} Icon`}
              title={`${svg?.svgAlt}`}
              style={{ width: `${svgWidth}px`, height: `${svgHeight}px` }}
            />
          )}

          {superheading && (
            <p className="text-superheading">{superheading}</p>
          )}

          {heading && (
            <>
            {aboveTheFold && isFirstSlide ? (
              <h1 className={`text-hero ${svg?.svgName && 'sr-only'}`}>{headingWithBreaks}</h1>
            ) : (
              <h2 className={`text-hero ${svg?.svgName && 'sr-only'}`}>{headingWithBreaks}</h2>
            )}         
            </>
          )}

          {subheading && <p className="mt-4">{subheading}</p>}
        </div>

        {button?.buttons?.length > 0 && (
          <ul
            className={`flex flex-row flex-wrap gap-4 mt-5 ${alignmentClasses} ${hiddenButtonClasses}`}
          >
            {button?.buttons?.slice(0, 2).map(({ link, style }, index) => {
              const buttonClasses = `${style} ${button?.sameSizeButtons ? 'w-44' : ''}`;
              return (
                <li key={index}>
                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                  <Link
                    aria-label={
                      link?.newTab 
                        ? `Open in a new tab to view ${link?.text}` 
                        : link?.text
                    }
                    className={buttonClasses}
                    href={link?.url}
                    newTab={link?.newTab}
                    type={link?.type}
                  >
                    {link?.text}
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {isActiveSlide && button?.clickableSlide && (
        <Link
          aria-label={button.buttons?.[0]?.link?.text}
          className="pointer-events-auto absolute inset-0 z-[1] h-full w-full"
          href={button.buttons?.[0]?.link?.url}
          type={button.buttons?.[0]?.link?.type}
        />
      )}
    </div>
  );
}

HeroContent.displayName = 'HeroContent';
